import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBetSlip } from '../../../components/feature/BetSlipContext';
import PageHero from '../../../components/feature/PageHero';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TeamRow {
  name: string;
  record: string;
  odds: string; // e.g. "+150" or "-180"
  prob: string; // e.g. "40%"
  price: string; // e.g. "$1.00"
  payout: string; // e.g. "$2.49"
  profit: string; // e.g. "+$1.49"
}

interface MoneylineCard {
  sport: string; // badge label e.g. "Football"
  league: string; // e.g. "NFL"
  gameTime: string; // e.g. "Tue, Jan 28, 3:00 PM"
  teamA: TeamRow;
  teamB: TeamRow;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const ALL_CARDS: MoneylineCard[] = [
  // NFL
  {
    sport: 'Football', league: 'NFL', gameTime: 'Tue, Jan 28, 3:00 PM',
    teamA: { name: 'Buffalo Bills', record: '13-4', odds: '+150', prob: '40%', price: '$1.00', payout: '$2.49', profit: '+$1.49' },
    teamB: { name: 'Kansas City Chiefs', record: '14-3', odds: '-180', prob: '64%', price: '$1.00', payout: '$1.55', profit: '+$0.55' },
  },
  {
    sport: 'Football', league: 'NFL', gameTime: 'Tue, Jan 28, 6:30 PM',
    teamA: { name: 'Philadelphia Eagles', record: '14-3', odds: '+120', prob: '45%', price: '$1.00', payout: '$2.19', profit: '+$1.19' },
    teamB: { name: 'San Francisco 49ers', record: '12-5', odds: '-140', prob: '58%', price: '$1.00', payout: '$1.70', profit: '+$0.70' },
  },
  {
    sport: 'Football', league: 'NFL', gameTime: 'Tue, Jan 28, 4:25 PM',
    teamA: { name: 'Green Bay Packers', record: '11-6', odds: '+165', prob: '38%', price: '$1.00', payout: '$2.64', profit: '+$1.64' },
    teamB: { name: 'Dallas Cowboys', record: '12-5', odds: '-195', prob: '66%', price: '$1.00', payout: '$1.50', profit: '+$0.50' },
  },
  // NBA
  {
    sport: 'Basketball', league: 'NBA', gameTime: 'Sun, Jan 26, 7:30 PM',
    teamA: { name: 'Milwaukee Bucks', record: '28-19', odds: '+180', prob: '36%', price: '$1.00', payout: '$2.79', profit: '+$1.79' },
    teamB: { name: 'Boston Celtics', record: '35-14', odds: '-220', prob: '69%', price: '$1.00', payout: '$1.44', profit: '+$0.44' },
  },
  {
    sport: 'Basketball', league: 'NBA', gameTime: 'Sun, Jan 26, 10:00 PM',
    teamA: { name: 'Denver Nuggets', record: '30-18', odds: '-130', prob: '57%', price: '$1.00', payout: '$1.76', profit: '+$0.76' },
    teamB: { name: 'Los Angeles Lakers', record: '26-19', odds: '+110', prob: '48%', price: '$1.00', payout: '$2.09', profit: '+$1.09' },
  },
  {
    sport: 'Basketball', league: 'NBA', gameTime: 'Mon, Jan 27, 10:30 PM',
    teamA: { name: 'Golden State Warriors', record: '23-23', odds: '+145', prob: '41%', price: '$1.00', payout: '$2.44', profit: '+$1.44' },
    teamB: { name: 'Phoenix Suns', record: '22-22', odds: '-170', prob: '63%', price: '$1.00', payout: '$1.58', profit: '+$0.58' },
  },
  {
    sport: 'Basketball', league: 'NBA', gameTime: 'Mon, Jan 27, 7:30 PM',
    teamA: { name: 'New York Knicks', record: '30-16', odds: '-115', prob: '53%', price: '$1.00', payout: '$1.86', profit: '+$0.86' },
    teamB: { name: 'Miami Heat', record: '23-22', odds: '-105', prob: '51%', price: '$1.00', payout: '$1.94', profit: '+$0.94' },
  },
  {
    sport: 'Basketball', league: 'NBA', gameTime: 'Mon, Jan 27, 8:30 PM',
    teamA: { name: 'Oklahoma City Thunder', record: '37-8', odds: '-105', prob: '51%', price: '$1.00', payout: '$1.94', profit: '+$0.94' },
    teamB: { name: 'Dallas Mavericks', record: '27-21', odds: '-115', prob: '53%', price: '$1.00', payout: '$1.86', profit: '+$0.86' },
  },
  // NHL
  {
    sport: 'Hockey', league: 'NHL', gameTime: 'Mon, Jan 27, 7:00 PM',
    teamA: { name: 'Boston Bruins', record: '27-18-5', odds: '-160', prob: '62%', price: '$1.00', payout: '$1.61', profit: '+$0.61' },
    teamB: { name: 'Toronto Maple Leafs', record: '26-16-6', odds: '+140', prob: '42%', price: '$1.00', payout: '$2.39', profit: '+$1.39' },
  },
  {
    sport: 'Hockey', league: 'NHL', gameTime: 'Mon, Jan 27, 9:00 PM',
    teamA: { name: 'Colorado Avalanche', record: '29-19-2', odds: '+105', prob: '49%', price: '$1.00', payout: '$2.04', profit: '+$1.04' },
    teamB: { name: 'Edmonton Oilers', record: '30-17-3', odds: '-125', prob: '56%', price: '$1.00', payout: '$1.79', profit: '+$0.79' },
  },
  {
    sport: 'Hockey', league: 'NHL', gameTime: 'Mon, Jan 27, 7:00 PM',
    teamA: { name: 'Florida Panthers', record: '28-17-5', odds: '+100', prob: '50%', price: '$1.00', payout: '$1.99', profit: '+$0.99' },
    teamB: { name: 'Tampa Bay Lightning', record: '26-18-6', odds: '-120', prob: '55%', price: '$1.00', payout: '$1.82', profit: '+$0.82' },
  },
  {
    sport: 'Hockey', league: 'NHL', gameTime: 'Mon, Jan 27, 10:00 PM',
    teamA: { name: 'Los Angeles Kings', record: '24-14-5', odds: '+155', prob: '39%', price: '$1.00', payout: '$2.54', profit: '+$1.54' },
    teamB: { name: 'Vegas Golden Knights', record: '28-14-3', odds: '-185', prob: '65%', price: '$1.00', payout: '$1.53', profit: '+$0.53' },
  },
];

const FILTERS = ['All', 'NFL', 'NBA', 'NHL'];

const SUBNAV = [
  { key: 'all', label: 'All', icon: 'ri-apps-2-line', href: '/markets' },
  { key: 'spreads', label: 'Spreads', icon: 'ri-bar-chart-grouped-line', href: '/markets/spreads' },
  { key: 'money-lines', label: 'Money Lines', icon: 'ri-copper-coin-line', href: '/markets/money-lines' },
  { key: 'futures', label: 'Futures', icon: 'ri-calendar-event-line', href: '/markets/futures' },
  { key: 'parlays', label: 'Parlays', icon: 'ri-stack-line', href: '/markets/parlays' },
  { key: 'player-props', label: 'Player Props', icon: 'ri-user-star-line', href: '/markets/player-props' },
];

const MARKET_SUB_LINKS = [
  { href: '/markets/spreads', icon: 'ri-bar-chart-grouped-line', label: 'Spreads' },
  { href: '/markets/money-lines', icon: 'ri-copper-coin-line', label: 'Money Lines' },
  { href: '/markets/futures', icon: 'ri-calendar-event-line', label: 'Futures' },
  { href: '/markets/parlays', icon: 'ri-stack-line', label: 'Parlays' },
  { href: '/markets/player-props', icon: 'ri-user-star-line', label: 'Player Props' },
];

// ─── Card Component ───────────────────────────────────────────────────────────

function MoneylineCardUI({ card }: { card: MoneylineCard }) {
  const [selectedTeam, setSelectedTeam] = useState<'A' | 'B' | null>('A');
  const [added, setAdded] = useState(false);
  const { addBet } = useBetSlip();

  const sportBadgeColor: Record<string, string> = {
    Football: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    Basketball: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    Hockey: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  };

  const badgeColor = sportBadgeColor[card.sport] || 'bg-slate-700 text-slate-300 border-slate-600';

  const selected = selectedTeam === 'B' ? card.teamB : card.teamA;

  const handleAddToSlip = () => {
    addBet({
      id: `ml-${card.gameTime}-${selected.name}`.replace(/\s+/g, '-').toLowerCase(),
      market: `${card.league} Money Line`,
      matchup: `${card.teamA.name} vs ${card.teamB.name}`,
      pick: `${selected.name} (${selected.odds})`,
      price: selected.price,
      payout: selected.payout,
      profit: selected.profit,
      badge: card.league,
      badgeColor,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  const renderOption = (team: typeof card.teamA, side: 'A' | 'B') => (
    <div
      key={side}
      onClick={() => setSelectedTeam(selectedTeam === side ? null : side)}
      className={`cursor-pointer px-3 py-2 rounded-lg border transition-all flex items-center justify-between ${
        selectedTeam === side
          ? 'bg-teal-500/10 border-teal-500/50'
          : 'bg-white/5 border-slate-700/60 hover:border-slate-500/60'
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className={`text-xs font-semibold truncate ${selectedTeam === side ? 'text-teal-300' : 'text-slate-300'}`}>
          {team.name}
        </div>
        <div className="text-xs text-slate-500">Prob: {team.prob} · {team.record}</div>
      </div>
      <div className="text-right pl-2 flex-shrink-0">
        <div className="text-xs text-slate-400">Bet: $1.00</div>
        <div className="text-xs text-teal-400 font-semibold">{team.profit}</div>
      </div>
    </div>
  );

  return (
    <div
      className="rounded-xl border border-slate-700/60 overflow-hidden flex flex-col"
      style={{ background: '#1a2235' }}
    >
      {/* Card Header */}
      <div
        className="flex items-center justify-between px-4 py-2.5 border-b border-slate-700/50"
        style={{ background: '#151e2e' }}
      >
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold px-2 py-0.5 rounded border ${badgeColor}`}>{card.league}</span>
          <span className="text-xs text-slate-400 font-medium">{card.sport}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <i className="ri-time-line text-teal-500"></i>
          <span className="text-teal-500 font-semibold">{card.gameTime}</span>
        </div>
      </div>

      {/* Title */}
      <div className="px-4 pt-3 pb-2">
        <h3 className="text-sm font-bold text-white leading-tight">
          {card.teamA.name} vs {card.teamB.name}
        </h3>
        <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500">
          <i className="ri-copper-coin-line text-emerald-400"></i>
          <span>Money Line · Pick the winner</span>
        </div>
      </div>

      {/* Options */}
      <div className="px-4 pb-3 flex-1">
        <div className="space-y-1.5">
          {renderOption(card.teamA, 'A')}
          {renderOption(card.teamB, 'B')}
        </div>
      </div>

      {/* Selected Summary */}
      <div className="px-4 py-2 border-t border-slate-700/40 flex items-center justify-between text-xs">
        <span className="text-slate-500">
          Bet $1.00 on{' '}
          <span className="text-slate-300 font-medium truncate max-w-[120px] inline-block align-bottom">
            {selected.name}
          </span>
        </span>
        <span className="text-teal-400 font-bold">Payout: {selected.payout}</span>
      </div>

      {/* Add to Slip */}
      <div className="px-4 pb-4 pt-1">
        <button
          onClick={handleAddToSlip}
          className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap border ${
            added
              ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
              : 'bg-teal-500/10 border-teal-500/30 text-teal-400 hover:bg-teal-500/20'
          }`}
        >
          <i className={`text-sm ${added ? 'ri-check-line' : 'ri-add-circle-line'}`}></i>
          {added ? 'Added!' : 'Add to Slip'}
        </button>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function MarketsMoneyLinesPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [marketsDropdownOpen, setMarketsDropdownOpen] = useState(false);
  const marketsDropdownRef = useRef<HTMLDivElement>(null);
  const { items: slipItems, toggleSlip } = useBetSlip();

  const filtered = activeFilter === 'all' ? ALL_CARDS : ALL_CARDS.filter((c) => c.sport === activeFilter);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (marketsDropdownRef.current && !marketsDropdownRef.current.contains(e.target as Node)) {
        setMarketsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-money-lines';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Money Lines — L2L Sports Prediction Market',
      description: 'Pick the winner straight up on NFL, NBA, and NHL games. Zero juice money line markets with peer-to-peer pricing.',
      url: `${siteUrl}/markets/money-lines`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Markets', item: `${siteUrl}/markets` },
          { '@type': 'ListItem', position: 3, name: 'Money Lines', item: `${siteUrl}/markets/money-lines` },
        ],
      },
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#0f1623' }}>
      {/* Demo Banner */}
      <div className="bg-amber-500 text-slate-900 px-4 py-2 relative z-[100]">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <i className="ri-alert-line text-base"></i>
          <span className="font-bold text-xs md:text-sm tracking-wide uppercase">
            Demo Only — Experience the Future of Sports Betting — From Sportsbook to Prediction Market
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg border-b border-slate-700/50" style={{ background: 'rgba(15,22,35,0.97)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 gap-4">
            <a className="flex items-center gap-2 flex-shrink-0" href="/">
              <div className="h-11 w-44 flex items-center justify-center">
                <img src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png" alt="RTJ Logo" className="h-9 w-auto object-contain" />
              </div>
            </a>
            <div className="hidden lg:flex items-center justify-center gap-1 flex-1">
              <a href="/"><div className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800/50 hover:text-white transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"><i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>Home</div></a>
              <a href="/march-madness"><div className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800/50 hover:text-white transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"><i className="ri-basketball-line w-4 h-4 flex items-center justify-center"></i>March Madness</div></a>
              <a href="/super-bowl-props"><div className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800/50 hover:text-white transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"><i className="ri-football-line w-4 h-4 flex items-center justify-center"></i>Super Bowl</div></a>
              <div className="relative" ref={marketsDropdownRef}>
                <button
                  onClick={() => setMarketsDropdownOpen((o) => !o)}
                  className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap flex items-center gap-2 bg-teal-500/20 text-teal-400 transition-all"
                >
                  <i className="ri-bar-chart-2-line w-4 h-4 flex items-center justify-center"></i>
                  Markets
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${marketsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {marketsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-52 rounded-xl border border-slate-700/80 shadow-2xl overflow-hidden z-50" style={{ background: 'rgba(15,22,35,0.99)' }}>
                    {MARKET_SUB_LINKS.map((link) => (
                      <a key={link.href} href={link.href} onClick={() => setMarketsDropdownOpen(false)}>
                        <div className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer ${link.href === '/markets/money-lines' ? 'bg-teal-500/10 text-teal-400' : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'}`}>
                          <i className={`${link.icon} w-4 h-4 flex items-center justify-center ${link.href === '/markets/money-lines' ? 'text-teal-400' : 'text-slate-500'}`}></i>
                          {link.label}
                        </div>
                      </a>
                    ))}
                    <div className="border-t border-slate-700/50">
                      <a href="/markets" onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-400 hover:bg-slate-800/60 hover:text-white transition-colors cursor-pointer">
                          <i className="ri-apps-2-line w-4 h-4 flex items-center justify-center text-slate-500"></i>
                          All Markets
                        </div>
                      </a>
                    </div>
                  </div>
                )}
              </div>
              <a href="/why-rtj"><div className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800/50 hover:text-white transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"><i className="ri-question-line w-4 h-4 flex items-center justify-center"></i>Why L2L</div></a>
              <a href="/my-account"><div className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800/50 hover:text-white transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"><i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>My Account</div></a>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-teal-500/10 rounded-lg border border-teal-500/30">
                <i className="ri-wallet-3-line text-teal-400 text-sm"></i>
                <span className="font-semibold text-white text-sm whitespace-nowrap">$10,000</span>
              </div>
              <button onClick={toggleSlip} className="relative p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition-colors cursor-pointer">
                <i className="ri-file-list-3-line text-teal-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                {slipItems.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-teal-500 text-white text-xs rounded-full flex items-center justify-center font-bold">{slipItems.length}</span>
                )}
              </button>
              <a href="/my-account">
                <div className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 cursor-pointer transition-colors">
                  <i className="ri-user-line text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
              </a>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors cursor-pointer"
              >
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-slate-700/50 py-3 space-y-1">
              {[
                { href: '/', icon: 'ri-home-line', label: 'Home' },
                { href: '/march-madness', icon: 'ri-basketball-line', label: 'March Madness' },
                { href: '/super-bowl-props', icon: 'ri-football-line', label: 'Super Bowl' },
                { href: '/markets', icon: 'ri-bar-chart-2-line', label: 'All Markets' },
                { href: '/markets/spreads', icon: 'ri-bar-chart-grouped-line', label: 'Spreads' },
                { href: '/markets/money-lines', icon: 'ri-copper-coin-line', label: 'Money Lines' },
                { href: '/markets/futures', icon: 'ri-calendar-event-line', label: 'Futures' },
                { href: '/markets/parlays', icon: 'ri-stack-line', label: 'Parlays' },
                { href: '/markets/player-props', icon: 'ri-user-star-line', label: 'Player Props' },
                { href: '/why-rtj', icon: 'ri-question-line', label: 'Why L2L' },
                { href: '/my-account', icon: 'ri-user-line', label: 'My Account' },
              ].map((item) => (
                <a key={item.href} href={item.href}>
                  <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800/50 hover:text-white transition-colors cursor-pointer">
                    <i className={`${item.icon} text-base`}></i>
                    <span className="text-sm font-medium">{item.label}</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <PageHero
        title="Money Lines"
        subtitle="Pick the winner straight up - No spreads, no complications"
        badgeLabel="Live Now"
        badgeColor="bg-emerald-500/20 border-emerald-500/40 text-emerald-400"
        backgroundImage="https://public.readdy.ai/ai/img_res/5413ffbc0bfdbf8b1bbd4565625836d5.jpg"
      />

      {/* Sub Navigation */}
      <div className="sticky top-[57px] z-40 border-b border-slate-700/50" style={{ background: 'rgba(15,22,35,0.98)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-none py-2">
            {SUBNAV.map((sub) => (
              <a key={sub.key} href={sub.href}>
                <div className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                  sub.key === 'money-lines'
                    ? 'bg-slate-700 text-white border border-slate-500/60'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
                }`}>
                  <i className={`${sub.icon} text-sm ${sub.key === 'money-lines' ? 'text-teal-400' : 'text-slate-500'}`}></i>
                  {sub.label}
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="border-b border-slate-700/50" style={{ background: 'rgba(30,30,30,0.9)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
          <div className="flex items-center gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`px-5 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  activeFilter === f
                    ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/25'
                    : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/50'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      <section className="py-6 px-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((card, idx) => (
              <MoneylineCardUI key={idx} card={card} />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-8 px-4 mt-8" style={{ background: '#0a1020' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-10 w-40 flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
              alt="RTJ Logo"
              className="h-8 w-auto object-contain"
            />
          </div>
          <p className="text-slate-600 text-xs text-center">
            Demo only. Not real money. For educational purposes only. Must be 18+ to participate.
          </p>
          <div className="flex items-center gap-4 text-slate-500 text-xs">
            <a href="/" className="hover:text-slate-300 transition-colors cursor-pointer">Home</a>
            <a href="/super-bowl-props" className="hover:text-slate-300 transition-colors cursor-pointer">Super Bowl</a>
            <a href="/march-madness" className="hover:text-slate-300 transition-colors cursor-pointer">March Madness</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
